import type {
  BiomeTheme,
  DeeplyNestedSx,
  MakeResponsive,
  OrderedSx,
  RemoveErrorBrand,
  SafeSizes,
} from "@/types";

import { isError } from "./textStyleHelpers";

export const inputResetStyles = (disabled?: boolean) => ({
  margin: "0",
  position: "absolute",
  top: "0",
  left: "0",
  width: "100%",
  height: "100%",
  cursor: disabled ? "default" : "pointer",
  border: "none",
  appearance: "none",
});

export function isSafeSize<T>(
  size: T,
  safeSizes: SafeSizes,
): size is NonNullable<RemoveErrorBrand<T>> {
  return typeof size === "string" && safeSizes.includes(size);
}

export function getStartingSize<T>(
  size: MakeResponsive<T>,
  defaultSize: T,
  safeSizes: SafeSizes,
) {
  const sizeAsArray = Array.isArray(size) ? [...size] : [size];
  const startingSize = sizeAsArray.shift();
  return isSafeSize(startingSize, safeSizes) ? startingSize : defaultSize;
}

export type RenderSxProps<S extends string> = {
  theme: BiomeTheme;
  size: S;
  [x: string]: unknown;
};

export function getResponsiveSx<S extends string>({
  theme,
  size,
  renderSxAtSize,
  ...props
}: {
  theme: BiomeTheme;
  size: MakeResponsive<S>;
  renderSxAtSize: (props: RenderSxProps<S>) => DeeplyNestedSx | OrderedSx;
}) {
  const sizeAsArray = Array.isArray(size) ? [...size] : [size];
  sizeAsArray.shift();

  const sizeStyles = Object.fromEntries(
    sizeAsArray
      .map((responsiveSize, index) => {
        if (responsiveSize != null && !isError(responsiveSize)) {
          const mediaStyleRule = `@media screen and (min-width: ${theme.base.breakpointAsArray?.[index]}px)`;
          return [
            mediaStyleRule,
            renderSxAtSize({
              size: responsiveSize,
              theme,
              ...props,
            }),
          ];
        }
        return [];
      })
      .filter(
        (entry): entry is [string, DeeplyNestedSx | OrderedSx] =>
          entry.length > 0,
      ),
  );

  return sizeStyles;
}

export function mergeSx(
  ...sxArgs: Array<DeeplyNestedSx | OrderedSx | undefined>
): OrderedSx {
  const flattenedSx = sxArgs
    .reduce<OrderedSx>((acc, currentSx) => {
      const sxToUse = currentSx ?? [];
      const sxAsArray = Array.isArray(sxToUse)
        ? [...sxToUse.flat(Number.POSITIVE_INFINITY)]
        : [sxToUse];
      return acc.concat(sxAsArray);
    }, [])
    // @NOTE: Filter out empty objects which may have been passed in
    .filter((item) => {
      if (typeof item !== "object" || item === null) return true;
      return Object.keys(item).length > 0;
    });
  return flattenedSx;
}
